// PropertyFeaturesBlock — Bloque compacto y reutilizable de "Características
// del inmueble" (property_features). Fuente única compartida por VENTA y
// ALQUILER; los cambios se persisten en el activo vía
// `PATCH /api/assets/{id}/property-features` y se reflejan en ambas pestañas.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Opciones visibles (mismas que ya existen en VENTA — no duplicar terminología).
export const PROPERTY_FEATURE_OPTIONS = {
  floor: [
    { value: 'GROUND', label: 'Planta baja' },
    { value: 'FIRST', label: '1.º' },
    { value: 'SECOND', label: '2.º' },
    { value: 'THIRD', label: '3.º' },
    { value: 'FOURTH', label: '4.º' },
    { value: 'FIFTH_OR_HIGHER', label: '5.º o superior' },
    { value: 'NOT_APPLICABLE', label: 'No aplica' },
    { value: 'UNKNOWN', label: 'Sin información' },
  ],
  elevator: [
    { value: 'YES', label: 'Sí' },
    { value: 'NO', label: 'No' },
    { value: 'UNKNOWN', label: 'Sin información' },
  ],
  pool: [
    { value: 'YES', label: 'Sí' },
    { value: 'NO', label: 'No' },
    { value: 'UNKNOWN', label: 'Sin información' },
  ],
  garage: [
    { value: 'YES', label: 'Sí' },
    { value: 'NO', label: 'No' },
    { value: 'UNKNOWN', label: 'Sin información' },
  ],
  condition: [
    { value: 'READY', label: 'Buen estado / listo para usar' },
    { value: 'LIGHT_RENOVATION', label: 'Reforma ligera' },
    { value: 'FULL_RENOVATION', label: 'Reforma integral' },
    { value: 'UNKNOWN', label: 'Sin información' },
  ],
};

// Migración no destructiva de legacy (valoracion_salida.*) → property_features.
// Sólo mapea si la equivalencia es clara; en caso de duda → UNKNOWN. Nunca
// interpreta vacío como "No".
const LEGACY_TO_FLOOR = {
  bajo: 'GROUND', primero: 'FIRST', segundo: 'SECOND', tercero: 'THIRD',
  cuarto: 'FOURTH', atico: 'FIFTH_OR_HIGHER',
};
const LEGACY_TO_CONDITION = {
  reformado: 'READY',
  buen_estado: 'READY',
  a_reformar: 'LIGHT_RENOVATION',
  reforma_integral: 'FULL_RENOVATION',
};
export const migrateLegacyToFeatures = (asset) => {
  const empty = { floor: 'UNKNOWN', elevator: 'UNKNOWN', pool: 'UNKNOWN', garage: 'UNKNOWN', condition: 'UNKNOWN' };
  if (!asset) return empty;
  // Calculamos primero los valores migrados desde legacy (siempre), y luego
  // las property_features guardadas prevalecen POR CAMPO (no en bloque),
  // para no perder los valores migrados de los campos que aún no se han tocado.
  const legacy = asset.valoracion_salida || {};
  const hasValoracion = !!asset.valoracion_salida;
  const floorLegacy = LEGACY_TO_FLOOR[legacy.planta] || 'UNKNOWN';
  let elevatorLegacy = 'UNKNOWN';
  if (hasValoracion && legacy.ascensor === 'si') elevatorLegacy = 'YES';
  else if (hasValoracion && legacy.ascensor === 'no') elevatorLegacy = 'NO';
  let poolLegacy = 'UNKNOWN';
  if (legacy.tiene_piscina === true) poolLegacy = 'YES';
  else if (hasValoracion && legacy.tiene_piscina === false) poolLegacy = 'NO';
  let garageLegacy = 'UNKNOWN';
  if (legacy.tiene_garaje === true) garageLegacy = 'YES';
  else if (hasValoracion && legacy.tiene_garaje === false) garageLegacy = 'NO';
  const conditionLegacy = LEGACY_TO_CONDITION[legacy.estado_inmueble] || 'UNKNOWN';
  const pf = asset.property_features && typeof asset.property_features === 'object'
    ? asset.property_features
    : {};
  return {
    floor: pf.floor || floorLegacy,
    elevator: pf.elevator || elevatorLegacy,
    pool: pf.pool || poolLegacy,
    garage: pf.garage || garageLegacy,
    condition: pf.condition || conditionLegacy,
  };
};

// Select estándar (Planta / Estado del inmueble).
const FeatureSelect = ({ label, featureKey, value, saving, readOnly, onChange }) => {
  const options = PROPERTY_FEATURE_OPTIONS[featureKey] || [];
  return (
    <div className="space-y-1" data-testid={`feature-${featureKey}`}>
      <label className="text-sm text-slate-600 font-medium flex items-center gap-2">
        {label}
        {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
      </label>
      <select
        value={value || 'UNKNOWN'}
        onChange={(e) => onChange(featureKey, e.target.value)}
        disabled={readOnly || saving}
        className="input-base w-full text-sm disabled:opacity-60"
        data-testid={`feature-${featureKey}-select`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
};

// Segmentado 3 estados (Ascensor / Piscina / Garaje).
const FeatureSegmented = ({ label, featureKey, value, saving, readOnly, onChange }) => {
  const options = PROPERTY_FEATURE_OPTIONS[featureKey] || [];
  const current = value || 'UNKNOWN';
  return (
    <div className="space-y-1" data-testid={`feature-${featureKey}`}>
      <label className="text-sm text-slate-600 font-medium flex items-center gap-2">
        {label}
        {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
      </label>
      <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden w-full" role="group">
        {options.map((o) => {
          const active = current === o.value;
          return (
            <button
              key={o.value}
              type="button"
              disabled={readOnly || saving}
              onClick={() => onChange(featureKey, o.value)}
              className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
              data-testid={`feature-${featureKey}-${o.value.toLowerCase()}`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Bloque completo reutilizable.
 * @param {object} asset  Activo actual (fuente inicial de datos).
 * @param {string} assetId
 * @param {boolean} readOnly
 * @param {'title'|'heading'|'none'} titleVariant  Estilo del título.
 * @param {boolean} showInfo  Muestra el texto informativo debajo del título.
 * @param {function} onSaved  Callback (key, value) tras confirmación backend.
 *                            VENTA lo usa para hacer write-through al form legacy.
 * @param {function} onAssetUpdate  Refresca el asset en el parent con el nuevo
 *                                  objeto (mantiene sincronía cross-pestaña).
 */
export const PropertyFeaturesBlock = ({
  asset, assetId, readOnly = false,
  titleVariant = 'heading', showInfo = true,
  onSaved, onAssetUpdate,
}) => {
  const [features, setFeatures] = useState(() => migrateLegacyToFeatures(asset));
  const [savingKey, setSavingKey] = useState(null);
  // Clave del último guardado exitoso (para mostrar "Guardado ✓" durante unos
  // segundos). Se limpia con un timer.
  const [savedKey, setSavedKey] = useState(null);

  // Sincroniza cuando cambia el asset externo (p.ej. edición desde otra
  // pestaña o refresh del parent).
  useEffect(() => {
    setFeatures(migrateLegacyToFeatures(asset));
  }, [asset]);

  // Auto-ocultar la confirmación de guardado tras unos segundos.
  useEffect(() => {
    if (!savedKey) return;
    const t = setTimeout(() => setSavedKey(null), 2800);
    return () => clearTimeout(t);
  }, [savedKey]);

  const handleChange = async (key, value) => {
    // Evitar guardado si el valor no ha cambiado realmente.
    if (features[key] === value) return;
    // NO optimista: mantenemos el valor anterior hasta que el backend confirme.
    setSavingKey(key);
    // Reflejo visual inmediato del selector (para que el usuario vea su elección),
    // pero SIN considerar "guardado" hasta la respuesta.
    setFeatures(prev => ({ ...prev, [key]: value }));
    try {
      const res = await axios.patch(
        `${API}/assets/${assetId}/property-features`,
        { [key]: value },
        { withCredentials: true },
      );
      const persisted = res.data?.property_features || {};
      setFeatures(prev => ({ ...prev, ...persisted }));
      setSavedKey(key);
      if (typeof onSaved === 'function') onSaved(key, value);
      if (typeof onAssetUpdate === 'function') {
        onAssetUpdate({ ...asset, property_features: persisted });
      }
    } catch (err) {
      // Rollback y aviso; no se muestra check.
      setFeatures(prev => ({ ...prev, [key]: asset?.property_features?.[key] ?? migrateLegacyToFeatures(asset)[key] }));
      const detail = err?.response?.data?.detail || 'No se pudo guardar';
      toast.error(typeof detail === 'string' ? detail : 'No se pudo guardar');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="property-features-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          {titleVariant === 'heading' && (
            <h4 className="text-sm font-semibold text-slate-600 pt-4 border-t border-slate-200">Características del inmueble</h4>
          )}
          {titleVariant === 'title' && (
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Características del inmueble</p>
          )}
          {showInfo && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              Estas características son comunes al activo. Los cambios también se reflejarán en VENTA.
            </p>
          )}
        </div>
        {/* Confirmación de guardado — cerca del bloque, discreta, auto-oculta */}
        {savedKey && (
          <span
            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
            data-testid="feature-saved-badge"
            role="status"
          >
            <Check className="w-3 h-3" />
            Guardado
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <FeatureSelect
          label="Planta" featureKey="floor"
          value={features.floor} saving={savingKey === 'floor'}
          readOnly={readOnly} onChange={handleChange}
        />
        <FeatureSegmented
          label="Ascensor" featureKey="elevator"
          value={features.elevator} saving={savingKey === 'elevator'}
          readOnly={readOnly} onChange={handleChange}
        />
        <FeatureSegmented
          label="Piscina" featureKey="pool"
          value={features.pool} saving={savingKey === 'pool'}
          readOnly={readOnly} onChange={handleChange}
        />
        <FeatureSegmented
          label="Garaje incluido" featureKey="garage"
          value={features.garage} saving={savingKey === 'garage'}
          readOnly={readOnly} onChange={handleChange}
        />
        <FeatureSelect
          label="Estado del inmueble" featureKey="condition"
          value={features.condition} saving={savingKey === 'condition'}
          readOnly={readOnly} onChange={handleChange}
        />
      </div>
    </div>
  );
};

export default PropertyFeaturesBlock;
